/**
 * Revenue pricing helpers.
 *
 * Maps subscription tier × billing cycle to monthly recurring revenue (EUR).
 * Source of truth: Stripe pricing alignment memory + Pricing.tsx.
 */

export type SubTier = 'starter' | 'pro' | 'enterprise' | 'welcome';
export type BillingCycle = 'monthly' | 'annual' | 'lifetime' | string;

/** Monthly normalized price in EUR for a (tier, cycle) pair. */
export function tierMonthlyPrice(tier: string | null | undefined, cycle: string | null | undefined): number {
  if (!tier) return 0;
  const t = tier.toLowerCase();
  const c = (cycle ?? '').toLowerCase();

  if (t === 'starter') {
    if (c === 'annual' || c === 'yearly') return 50 / 12;
    if (c === 'monthly') return 5;
    return 5;
  }
  if (t === 'pro') {
    if (c === 'annual' || c === 'yearly') return 190 / 12;
    if (c === 'monthly') return 19;
    return 19;
  }
  if (t === 'enterprise') {
    // Enterprise is monthly-only in current pricing
    return 99;
  }
  // welcome / unknown / lifetime → no recurring revenue
  return 0;
}

/** Full charge amount in EUR for one billing period (used for "revenue this month" attribution). */
export function tierPeriodPrice(tier: string | null | undefined, cycle: string | null | undefined): number {
  if (!tier) return 0;
  const t = tier.toLowerCase();
  const c = (cycle ?? '').toLowerCase();

  if (t === 'starter') {
    if (c === 'annual' || c === 'yearly') return 50;
    return 5;
  }
  if (t === 'pro') {
    if (c === 'annual' || c === 'yearly') return 190;
    return 19;
  }
  if (t === 'enterprise') return 99;
  return 0;
}

export function isPaidTier(tier: string | null | undefined): boolean {
  if (!tier) return false;
  const t = tier.toLowerCase();
  return t === 'starter' || t === 'pro' || t === 'enterprise';
}

export function formatEUR(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(Math.round(value));
}

export function formatEURPrecise(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(value);
}
